import detectEthereumProvider from "@metamask/detect-provider"
import { BigNumberish, BrowserProvider, Contract, ethers, Interface, InterfaceAbi, JsonRpcProvider, parseUnits } from "ethers"

import ERC20 from "@/abi/ERC20.json"

interface ContractResponseError extends Error {
    error: Error
    reason?: string
}

export async function useWeb3Provider() {
    let ethereumProvider = await detectEthereumProvider() as Ethereum
    return {
        ethereumProvider,
        Web3Provider: ethereumProvider ? new BrowserProvider(ethereumProvider) : null
    }
}

export async function useContract(address: string, ABI: InterfaceAbi | Interface): Promise<{
    Contract: Contract | null
    Web3Provider: BrowserProvider | null
}> {
    const { Web3Provider } = await useWeb3Provider()
    if (Web3Provider === null) {
        return {
            Contract: null,
            Web3Provider
        }
    }

    const signer = await Web3Provider.getSigner();
    return {
        Contract: new Contract(address, ABI, signer),
        Web3Provider
    }
}

export async function useContractApprove(web3Provider: BrowserProvider, contract: Contract, spender: string, amount: number) {
    return new Promise(async (resolve, reject) => {
        const singer = await web3Provider.getSigner()
        let owner = await singer.getAddress()
        contract.allowance(owner, spender).then(async (num: BigNumberish) => {
            let decimals: number = await contract.decimals()
            let allowance = +ethers.formatUnits(num, decimals)
            console.log("allowance: ", allowance)

            if (amount > allowance) {
                console.log("start approve: ")
                contract.approve(
                    spender,
                    ethers.parseUnits(amount.toString(), decimals)
                ).then((response: { wait(): Promise<unknown> }) => {
                    response.wait().then(resolve)
                }).catch((err: ContractResponseError) => {
                    err.reason ? reject({message: err.reason}) : resolve("")
                })
            }
            else {
                resolve(0)
            }
        }).catch((err: ContractResponseError) => {
            reject(err)
        })
    })
}

export const Tron = () => {
    if (window.tron) {
        return window.tron
    }
    return null
}

export const TronLink = () => {
    if (window.tronLink) {
        return window.tronLink as TronLink
    }
    return null
}

export const TronWeb = () => {
    if (window.tronWeb) {
        return window.tronWeb as TronWeb
    }
    return null
}

const isTrustWallet = (ethereum: any) => {
    // Identify if Trust Wallet injected provider is present.
    const trustWallet = !!ethereum.isTrust;

    return trustWallet;
};

export async function getTrustWalletInjectedProvider(
    { timeout } = { timeout: 3000 }
  ) {
    const provider = TrustWallet();
  
    if (provider) {
      return provider;
    }
  
    return listenForTrustWalletInitialized({ timeout });
  }
  
  async function listenForTrustWalletInitialized(
    { timeout } = { timeout: 3000 }
  ) {
    return new Promise((resolve) => {
      const handleInitialization = () => {
        resolve(TrustWallet());
      };
  
      window.addEventListener("trustwallet#initialized", handleInitialization, {once: true});
  
      setTimeout(() => {
        window.removeEventListener(
          "trustwallet#initialized",
          handleInitialization,
        );
        resolve(null);
      }, timeout);
    });
  }

export const TrustWallet = (): Trust | null => {
    const injectedProviderExist =
    typeof window !== "undefined" && typeof window.ethereum !== "undefined";
    
    // No injected providers exist.
    if (!injectedProviderExist) {
        return null;
    }
    
    // Trust Wallet was injected into window.ethereum.
    if (isTrustWallet(window.ethereum)) {
        return window.ethereum;
    }
    
    // Trust Wallet provider might be replaced by another
    // injected provider, check the providers array.
    if (window.ethereum?.providers) {
        // ethereum.providers array is a non-standard way to
        // preserve multiple injected providers. Eventually, EIP-5749
        // will become a living standard and we will have to update this.
        return window.ethereum.providers.find(isTrustWallet) ?? null;
    }
    
    // Trust Wallet injected provider is available in the global scope.
    // There are cases that some cases injected providers can replace window.ethereum
    // without updating the ethereum.providers array. To prevent issues where
    // the TW connector does not recognize the provider when TW extension is installed,
    // we begin our checks by relying on TW's global object.
    return window["trustwallet"] ?? null;
}

export async function useTronContract(tronWeb: TronWeb, address: string): Promise<any> {
    return await tronWeb.contract(ERC20 as any, address)
}

export async function useTronContractApprove(contract: any, owner: string, spender: string, amount: number) {
    return new Promise(async (resolve, reject) => {
        let source = await contract.allowance(owner, spender).call()
        let decimals = await contract.decimals().call()
        const decimal = parseInt(decimals._hex, 16)
        let allowance = parseInt(source._hex, 16) / decimal

        if (amount > allowance) {
            console.log("approve amount", parseUnits(amount.toString(), decimal))
            contract.approve(
                spender,
                parseUnits(amount.toString(), decimal)
            ).send({
                feeLimit: 100_000_000,
                callValue: 0,
            }).then(resolve).catch((err: any) => {
                console.log(err)
                reject(err)
            })
        }
        else {
            resolve(0)
        }
    })
}

export async function getNonce(rpcUrl: string, address: string) {
    const provider = new JsonRpcProvider(rpcUrl);
    const nonce = await provider.getTransactionCount(address);
    console.log(`当前 nonce (${rpcUrl}):`, nonce);
    return nonce
}
